import { useMemo } from "react";
import { Link, generatePath, useOutletContext } from "react-router-dom";
import cn from "classnames";
import * as Team from "src/state/modules/team";
import * as Event from "src/state/modules/event";
import * as Eden from "src/eden/content";
import { ROUTES } from "src/utils/constants/routes";
import {
  useGetTeamEventAndSummariesByIDQuery,
  useGetTeamLiveGamesByIDQuery,
  useGetTeamEventLiveStreamQuery,
} from "src/state/modules/api";
import { overlayLiveGames, formatEventScore, formatEventTitle, formatLiveMeta } from "src/utils/map-events";
import { DATE_FORMAT, dateComparator, formatDate, getBrowserTimezone, getNow } from "src/utils/date-utils";
import LivePlayer from "src/components/compounds/LivePlayer/LivePlayer";
import LiveScoreboard from "src/components/compounds/LiveScoreboard/LiveScoreboard";
import HotStreaks from "src/components/compounds/HotStreaks/HotStreaks";
import { Avatar } from "src/components/atoms/Avatar/Avatar";
import { Text } from "src/components/atoms/Text/Text";
import PageLoader1 from "src/components/atoms/PageLoader/PageLoader";
import baseballBg from "src/images/live-game-bg-baseball.jpg";
import softballBg from "src/images/live-game-bg-softball.jpg";
import Styles from "./TeamHomePage.module.scss";

// Keep the live status (score / availability / viewer count) fresh while the page is open,
// matching the schedule's live polling. skipPollingIfUnfocused avoids polling background tabs.
const LIVE_POLL_MS = 30000;
const MAX_UPCOMING = 3;

const sportBackground = (sport?: Team.Sport): string => (sport === Team.Sport.SOFTBALL ? softballBg : baseballBg);

const TeamHomePage = () => {
  const team = useOutletContext<Team.Team>();
  if (!team) return <PageLoader1 />;
  return (
    <div className={Styles.home}>
      <UpcomingGames team={team} />
      <HotStreaks team={team} />
      <LiveSection team={team} />
    </div>
  );
};

// ---- Schedule preview ------------------------------------------------------

const formatTime = (event: Event.Event, timezone: string): string =>
  event.isFullDay ? "All Day" : formatDate(event.start, timezone, DATE_FORMAT.time);

const GameCard = ({ team, event }: { team: Team.Team; event: Event.Event }) => {
  const timezone = getBrowserTimezone();
  const isLive = event.gameData?.game_state === Event.GameState.LIVE;
  const score = formatEventScore(event);
  // Completed/live games open on Box Score; upcoming games (no game data → no box score) on Info.
  const eventRoute = event.gameData ? ROUTES.teams.events.boxScore : ROUTES.teams.events.scheduleEvent;
  return (
    <Link
      to={generatePath(eventRoute, { id: team.id, eid: event.id })}
      className={Styles.gameCard}
      data-testid="home-game-card"
    >
      <div className={Styles.gameCardHeader}>
        <Text size="small" color="cool-grey-dark" weight="semibold" className={Styles.dateChip}>
          {formatDate(event.start, timezone, DATE_FORMAT.dayAndMonthAndDay)}
        </Text>
        {isLive && <span className={Styles.liveTag}>LIVE</span>}
      </div>
      <div className={Styles.gameCardBody}>
        <Avatar.Team className={Styles.gameAvatar} sportIcon={team.sport ?? Team.Sport.BASEBALL} size="small" />
        <Text weight="semibold" className={Styles.gameTitle}>
          {formatEventTitle(event)}
        </Text>
        <Text size="small" color="cool-grey-dark" weight="semibold" className={Styles.gameMeta}>
          {score ?? formatTime(event, timezone)}
        </Text>
      </div>
    </Link>
  );
};

const UpcomingGames = ({ team }: { team: Team.Team }) => {
  const { data: events, isLoading } = useGetTeamEventAndSummariesByIDQuery(team.id);
  const { data: liveGames } = useGetTeamLiveGamesByIDQuery(team.id, {
    pollingInterval: LIVE_POLL_MS,
    skipPollingIfUnfocused: true,
  });

  const upcoming = useMemo(() => {
    if (!events) return [];
    const now = getNow();
    const merged = overlayLiveGames([...events], liveGames ?? []);
    // Canceled events aren't "upcoming" — drop them so the preview only shows
    // events that will actually happen (the full Schedule tab still lists them).
    const sorted = merged
      .filter((e) => e.status !== "canceled")
      .sort((a, b) => dateComparator(a.start, b.start));
    const liveIds = new Set((liveGames ?? []).filter((g) => g.is_live).map((g) => g.event_id));
    const future = sorted.filter((e) => liveIds.has(e.id) || dateComparator(e.start, now) >= 0);
    // Prefer live + upcoming games; if the season is over, fall back to the most recent games.
    return (future.length > 0 ? future : sorted.slice(-MAX_UPCOMING)).slice(0, MAX_UPCOMING);
  }, [events, liveGames]);

  return (
    <section className={Styles.section}>
      <Text block size="xlarge" weight="xbold" className={Styles.sectionTitle}>
        Schedule
      </Text>
      {isLoading ? (
        <PageLoader1 />
      ) : upcoming.length === 0 ? (
        <Text block color="cool-grey-dark" className={Styles.emptyState}>
          No upcoming events scheduled.
        </Text>
      ) : (
        <div className={Styles.gameCards}>
          {upcoming.map((event) => (
            <GameCard key={event.id} team={team} event={event} />
          ))}
        </div>
      )}
    </section>
  );
};

// ---- Live video ------------------------------------------------------------

// Shared empty-state stage. Shows the play glyph for the generic "no videos" state, or a red
// LIVE badge when a game is in progress but not broadcasting video (badge + score meta line).
const LivePlaceholder = ({
  team,
  headline,
  subhead,
  meta,
  badge = false,
  testId = "home-live-placeholder",
}: {
  team: Team.Team;
  headline: string;
  subhead: string;
  meta?: string;
  badge?: boolean;
  testId?: string;
}) => (
  <div
    className={cn(Styles.mediaBox, Styles.placeholder)}
    style={{ backgroundImage: `url(${sportBackground(team.sport)})` }}
    data-testid={testId}
  >
    <div className={Styles.placeholderScrim} />
    <div className={Styles.placeholderContent}>
      {badge ? (
        <span className={cn(Styles.liveBadge, Styles.placeholderBadge)}>● LIVE</span>
      ) : (
        <span className={Styles.playButton}>
          <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" aria-hidden="true">
            <path d="M8 5v14l11-7z" />
          </svg>
        </span>
      )}
      <Text block weight="xbold" color="white" className={Styles.placeholderHeadline}>
        {headline}
      </Text>
      {meta && (
        <Text block weight="semibold" color="white" className={Styles.placeholderMeta}>
          {meta}
        </Text>
      )}
      <Text block weight="semibold" color="white" className={Styles.placeholderSubhead}>
        {subhead}
      </Text>
    </div>
  </div>
);

const LiveSection = ({ team }: { team: Team.Team }) => {
  const { data: liveGames } = useGetTeamLiveGamesByIDQuery(team.id, {
    pollingInterval: LIVE_POLL_MS,
    skipPollingIfUnfocused: true,
  });

  const liveGame: Eden.LiveGame | undefined = (liveGames ?? []).find((g) => g.is_live);

  const { data: liveStream } = useGetTeamEventLiveStreamQuery(
    { teamID: team.id, eventID: liveGame?.event_id ?? "" },
    { skip: !liveGame, pollingInterval: LIVE_POLL_MS, skipPollingIfUnfocused: true }
  );

  const isPlayable = !!liveGame && !!liveStream?.is_playable;

  return (
    <section className={Styles.section}>
      <Text block size="xlarge" weight="xbold" className={Styles.sectionTitle}>
        Live
      </Text>
      {isPlayable && liveGame && liveStream ? (
        <>
          <div className={Styles.liveHeader}>
            <span className={Styles.liveBadge}>● LIVE</span>
            <Text size="small" color="cool-grey-dark" weight="semibold">
              {`${liveStream.viewer_count} watching`}
            </Text>
          </div>
          <div className={Styles.mediaBox} data-testid="home-live-player">
            <LivePlayer teamId={team.id} eventId={liveGame.event_id} poster={liveStream.thumbnail_url} />
            <LiveScoreboard team={team} eventId={liveGame.event_id} />
          </div>
        </>
      ) : liveGame ? (
        // Game is being scored live, but no one is broadcasting video (GameChanger has a
        // game-stream for scoring but no video-stream). Show the live score, not "no videos".
        <LivePlaceholder
          team={team}
          badge
          headline="Live now"
          meta={formatLiveMeta(liveGame)}
          subhead="Video isn't available for this game"
          testId="home-live-no-video"
        />
      ) : (
        <LivePlaceholder team={team} headline="NO VIDEOS YET!" subhead="Live the game like never before" />
      )}
    </section>
  );
};

export default TeamHomePage;
